import React, { createContext, useContext } from 'react'
import clsx from 'clsx'
import type * as Polymorphic from '@radix-ui/react-polymorphic'

import { UnstyledButton } from '../UnstyledButton'
import { LoadingIcon } from '../icons'
import { Separator } from '../Separator'

import styles from './Button.module.css'

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'critical'
  | 'critical-subtle'
  | 'success'
  | 'warning'
  | 'upgrade'

type ButtonSize = 'large' | 'medium' | 'small'

type ButtonProps = {
  /**
   * Controls visual style of the Button
   * @default primary
   */
  variant?: ButtonVariant
  /**
   * Controls size of the Button
   * @default medium
   */
  size?: ButtonSize
  /**
   * Controls whether the Button is disabled, disabled buttons should be used sparingly,
   * @default false
   */
  disabled?: boolean

  /**
   * Controls whether the Button is inactive while loading
   * @default false
   */
  loading?: boolean
  /**
   * Additional classname applied to the Button
   */
  className?: string

  /**
   * Content to render inside the button, typically a label
   */
  children: React.ReactNode
  /**
   * On click handler
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

type PolymorphicButton = Polymorphic.ForwardRefComponent<'button', ButtonProps>

type ButtonGroupContextValue = {
  size?: ButtonSize
  variant?: ButtonVariant
  disabled?: boolean
}

const ButtonGroupContext = createContext<ButtonGroupContextValue | null>(null)

const useButtonGroupContext = () => useContext(ButtonGroupContext)

type ButtonGroupProps = {
  /**
   * Buttons to be rendered inside the group
   */
  children: React.ReactNode
  /**
   * Additional classname applied to the ButtonGroup
   */
  className?: string
  /**
   * Controls size of all buttons in the group (can be overridden by individual buttons)
   */
  size?: ButtonSize
  /**
   * Controls visual style of all buttons in the group (can be overridden by individual buttons)
   */
  variant?: ButtonVariant
  /**
   * Controls whether all buttons in the group are disabled (can be overridden by individual buttons)
   */
  disabled?: boolean
}

/**
 * Button.Group component that allows grouping buttons together with shared props.
 * All buttons within a group will inherit the group's size, variant, and disabled props unless explicitly overridden.
 */
const ButtonGroup = ({
  children,
  className,
  size = 'medium',
  variant,
  disabled,
}: ButtonGroupProps): JSX.Element => {
  const childrenArray = React.Children.toArray(children)
  const contextValue = {
    size,
    variant,
    disabled,
  }

  return (
    <ButtonGroupContext.Provider value={contextValue}>
      <div className={clsx(styles.group, className)}>
        {React.Children.map(childrenArray, (child, index) => {
          const isLastChild = index === childrenArray.length - 1
          const nextChild = childrenArray[index + 1]

          const buttonElement = child

          if (isLastChild) {
            return buttonElement
          }

          // Use the child's key if available, otherwise fallback to index
          const key =
            React.isValidElement(child) && child.key
              ? child.key
              : `button-group-${index}`

          return (
            <React.Fragment key={key}>
              {buttonElement}
              {React.isValidElement(nextChild) && (
                <Separator
                  orientation="vertical"
                  decorative
                  className={clsx(
                    styles.separator,
                    styles[`separator-${size}`],
                  )}
                />
              )}
            </React.Fragment>
          )
        })}
      </div>
    </ButtonGroupContext.Provider>
  )
}

ButtonGroup.displayName = 'Button.Group'

type ButtonComponent = PolymorphicButton & {
  Group: React.FC<ButtonGroupProps>
}

/**
 * Primary UI component for user interaction. In case you need an icon-only button, use `IconButton` instead.
 */
const Button = React.forwardRef(
  (
    {
      variant,
      size,
      children,
      loading,
      disabled,
      className,
      ...props
    }: ButtonProps,
    forwardedRef,
  ) => {
    const groupContext = useButtonGroupContext()

    // Use explicitly defined props first, then group context props, then defaults
    // Notice we're not setting defaults here anymore to correctly detect undefined values
    const finalVariant =
      variant !== undefined
        ? variant
        : groupContext?.variant !== undefined
        ? groupContext.variant
        : 'primary'

    const finalSize =
      size !== undefined
        ? size
        : groupContext?.size !== undefined
        ? groupContext.size
        : 'medium'

    const finalDisabled =
      disabled !== undefined
        ? disabled
        : groupContext?.disabled !== undefined
        ? groupContext.disabled
        : false

    return (
      <UnstyledButton
        className={clsx(
          styles.base,
          loading && styles.loading,
          styles[finalSize],
          styles[finalVariant],
          className,
        )}
        // TODO: This is temporary, disabled buttons are not focusable by default
        // which makes them not accessible. We should revisit it and make sure
        // that the button is focusable when disabled or loading
        disabled={loading || finalDisabled}
        ref={forwardedRef}
        {...props}
      >
        {loading && <LoadingIcon />}
        {children}
      </UnstyledButton>
    )
  },
) as ButtonComponent

Button.displayName = 'Button'
Button.Group = ButtonGroup

export { Button, useButtonGroupContext }
export type { ButtonProps, ButtonVariant, ButtonSize, ButtonGroupProps }
